// GDPR cookie-consent state. Persists user choice in localStorage and dispatches a
// "wavbee:consent" event whenever the choice changes so listeners (analytics
// loaders, pixel snippets) can react. No external dependencies.

export type ConsentCategory = 'essential' | 'analytics' | 'marketing'

export interface ConsentState {
  essential: true
  analytics: boolean
  marketing: boolean
  /** ISO timestamp of the user's decision; null = no decision yet */
  decided_at: string | null
}

const STORAGE_KEY = 'wavbee_cookie_consent_v1'
const EVENT_NAME  = 'wavbee:consent'

const DEFAULT_STATE: ConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  decided_at: null,
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

export function readConsent(): ConsentState {
  if (!isBrowser()) return DEFAULT_STATE
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_STATE
    const parsed = JSON.parse(raw)
    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      decided_at: typeof parsed.decided_at === 'string' ? parsed.decided_at : null,
    }
  } catch {
    return DEFAULT_STATE
  }
}

export function hasDecided(): boolean {
  return readConsent().decided_at !== null
}

export function setConsent(next: Partial<Pick<ConsentState, 'analytics' | 'marketing'>>): void {
  if (!isBrowser()) return
  const state: ConsentState = {
    essential: true,
    analytics: Boolean(next.analytics),
    marketing: Boolean(next.marketing),
    decided_at: new Date().toISOString(),
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    window.dispatchEvent(new CustomEvent<ConsentState>(EVENT_NAME, { detail: state }))
  } catch {
    /* ignore quota errors */
  }
}

export function acceptAll(): void {
  setConsent({ analytics: true, marketing: true })
}

export function rejectAll(): void {
  setConsent({ analytics: false, marketing: false })
}

export function onConsentChange(handler: (state: ConsentState) => void): () => void {
  if (!isBrowser()) return () => {}
  const wrapped = (e: Event) => handler((e as CustomEvent<ConsentState>).detail)
  window.addEventListener(EVENT_NAME, wrapped)
  return () => window.removeEventListener(EVENT_NAME, wrapped)
}
